'use client';

import { User } from '@supabase/supabase-js';
import React, { createContext, useContext, useEffect, useState } from 'react';

import {
  signIn as signInAction,
  signOut as signOutAction,
  verifyOTP as verifyOTPAction,
} from '../actions/auth';
import { redirect } from '../i18n/navigation';
import { createClient } from '../lib/supabase/client';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  signIn: (data: { email: string }) => Promise<void>;
  verifyOTP: (data: { email: string; token: string }) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();

    const initializeAuth = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user ?? null);
      setIsLoading(false);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const user = session?.user;
      setUser(user ?? null);
    });

    initializeAuth();

    return () => subscription.unsubscribe();
  }, []);

  async function signIn({ email }: { email: string }) {
    const formData = new FormData();
    formData.append('email', email);
    await signInAction(formData);
  }

  async function verifyOTP({ email, token }: { email: string; token: string }) {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('token', token);
    const user = await verifyOTPAction(formData);
    setUser(user);
  }

  async function signOut() {
    await signOutAction();
    setUser(null);
    redirect('/');
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        signIn,
        verifyOTP,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
